import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import BlogList from './BlogList';
import BlogPost from './BlogPost';
import PandaReflexes from './posts/PandaReflexes';
import ByokPlan from './posts/ByokPlan';
import ChatReplyPost from './posts/ChatReplyPost';
import Changelog from './Changelog';
import Privacy from './Privacy';
import About from './About';
import YtSkipHelper from './YtSkipHelper';
import MapsRideHelper from './MapsRideHelper';
import ChatReplyHelper from './ChatReplyHelper';
import Navbar from './Navbar';
import ScrollToTop from './ScrollToTop';
import GeometricBackground from './GeometricBackground';
import Download from './Download';
import DownloadBeta from './DownloadBeta';
import MobileAppPreview from './MobileAppPreview';
import TrustedByCounter from './TrustedByCounter';

const playStoreUrl = "https://play.google.com/store/apps/details?id=com.blurr.voice";

function HomePage() {
  return (
    <div className="App new-homepage">
      <section className="new-hero">
        <div className="new-hero-inner">
          <motion.div className="new-hero-badge" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="new-hero-badge-text">Voice Powered Agent</span>
            <span className="new-hero-badge-bars"><span /><span /></span>
          </motion.div>

          <motion.h1 className="new-hero-heading" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
            <span className="new-hero-line1">THE AUTONOMOUS AGENT</span>
            <span className="new-hero-line2"><span className="new-hero-dash" aria-hidden="true"></span>FOR YOUR SMARTPHONE<span className="new-hero-dot">.</span></span>
          </motion.h1>

          <motion.div className="new-hero-actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <a href={playStoreUrl} target="_blank" rel="noopener noreferrer" className="clip-btn clip-btn-accent" id="new-playstore-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', position: 'relative', top: '-1px' }}>
                <polygon points="5 3 20 12 5 21 5 3"></polygon><line x1="5" y1="3" x2="15" y2="15"></line><line x1="5" y1="21" x2="15" y2="9"></line>
              </svg>
              GET ON PLAY STORE
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}>
            <TrustedByCounter />
          </motion.div>

          <motion.div
            className="new-hero-trust"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            aria-label="Backed by grants from Meta, IndiaAI, AICTE, and 1M1B"
          >
            <span className="trust-label">BACKED BY GRANTS FROM</span>
            <p className="sr-only">Backed by grants from Meta, IndiaAI, AICTE, and 1M1B.</p>
            <div className="trust-logos">
              <img src="/logos/meta.svg" className="trust-logo trust-logo-meta" alt="Meta" />
              <img src="/logos/indiaai.png" className="trust-logo trust-logo-indiaai" alt="IndiaAI" />
              <img src="/logos/aicte.svg" className="trust-logo trust-logo-aicte" alt="AICTE" />
              <img src="/logos/1m1b.svg" className="trust-logo trust-logo-1m1b" alt="1M1B" />
            </div>
          </motion.div>
        </div>
        <div className="new-hero-footer">Panda is a product of Steepwind Labs</div>
      </section>
    </div>
  );
}

// Wrap app content to use location for routing and tab titles
function AppContent() {
  const location = useLocation();

  // Dynamically update the browser tab title based on the current URL
  useEffect(() => {
    switch (location.pathname) {
      case '/':
        document.title = "Steepwind Labs | Panda, AI assistant for Android";
        break;
      case '/about':
        document.title = "About Steepwind Labs | Panda";
        break;
      case '/privacy-policy':
      case '/privacy':
        document.title = "Privacy Policy | Panda";
        break;
      case '/blog':
        document.title = "Blog | Panda";
        break;
      case '/blog/panda-gets-reflexes':
        document.title = "Panda Gets Reflexes | Steepwind Labs";
        break;
      case '/blog/how-byok-works':
        document.title = "How the BYOK plan works | Steepwind Labs";
        break;
      case '/blog/chat-reply':
        document.title = "A reply, ready when the keyboard opens | Steepwind Labs";
        break;
      case '/changelog':
        document.title = "Changelog | Panda";
        break;
      case '/download':
        document.title = "Install | Panda";
        break;
      case '/download-beta':
        document.title = "Download Beta | Panda";
        break;
      case '/why-yt-a11y-skip-helper':
        document.title = "YouTube Skip Helper | Panda";
        break;
      case '/why-maps-ride-helper':
        document.title = "Maps Ride Helper | Panda";
        break;
      case '/why-chat-reply-helper':
        document.title = "Chat Reply Helper | Panda";
        break;
      case '/mobile-app-preview':
        document.title = "Mobile app preview | Panda";
        break;
      default:
        document.title = "Panda - Your AI Assistant";
    }
  }, [location]);

  const isWebViewEmbed = location.pathname === '/mobile-app-preview';

  useEffect(() => {
    const summary = document.getElementById('static-site');
    if (summary) summary.hidden = true;
  }, []);

  return (
    <>
      {!isWebViewEmbed && (
        <div className="global-bg-wrapper">
          <GeometricBackground />
        </div>
      )}
      {!isWebViewEmbed && <Navbar />}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><HomePage /></motion.div>} />
          <Route path="/blog" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><BlogList /></motion.div>} />
          <Route path="/blog/panda-gets-reflexes" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><PandaReflexes /></motion.div>} />
          <Route path="/blog/how-byok-works" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><ByokPlan /></motion.div>} />
          <Route path="/blog/chat-reply" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><ChatReplyPost /></motion.div>} />
          <Route path="/changelog" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><Changelog /></motion.div>} />
          <Route path="/blog/:id" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><BlogPost /></motion.div>} />
          <Route path="/privacy-policy" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><Privacy /></motion.div>} />
          <Route path="/privacy" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><Privacy /></motion.div>} />
          <Route path="/about" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><About /></motion.div>} />
          <Route path="/why-yt-a11y-skip-helper" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><YtSkipHelper /></motion.div>} />
          <Route path="/why-maps-ride-helper" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><MapsRideHelper /></motion.div>} />
          <Route path="/why-chat-reply-helper" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><ChatReplyHelper /></motion.div>} />
          <Route path="/download" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><Download /></motion.div>} />
          <Route path="/download-beta" element={<motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}><DownloadBeta /></motion.div>} />
          <Route path="/mobile-app-preview" element={<MobileAppPreview />} />
        </Routes>
      </AnimatePresence>
    </>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}

export default App;